import { useEffect, useState } from "react";
import { api, ApiError, type MeResponse } from "../api/client";
import { ProgressBar } from "../components/ProgressBar";
import { formatBytes, formatCount, formatTier, safeNumber, tierTagline } from "../lib/format";

const DOCS_URL = "https://docs.sequesign.com";
const SDK_URL = "https://docs.sequesign.com/sdk";

export function DashboardPage(props: { onNavigate: (path: string) => void }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.me();
        if (!cancelled) setMe(data);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message);
        else setError("Could not load dashboard.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error && !me) {
    return (
      <div className="panel">
        <h2 className="section-title">Dashboard</h2>
        <div className="error">{error}</div>
      </div>
    );
  }
  if (!me) return <p className="muted">Loading…</p>;

  const { customer, current_period: period } = me;
  const signaturesUsed = safeNumber(period?.signatures_used);
  const libraryBytesUsed = safeNumber(period?.library_bytes_used);
  const isFirstSignature = signaturesUsed === 0;

  return (
    <>
      {isFirstSignature ? (
        <div className="panel welcome-panel">
          <h2 className="section-title">Ready when you are</h2>
          <p>
            Sign your first receipt. See{" "}
            <a href={DOCS_URL} target="_blank" rel="noreferrer">
              the docs
            </a>{" "}
            for the 5-minute setup.
          </p>
        </div>
      ) : null}

      <div className="panel">
        <h2 className="section-title">Overview</h2>
        <div className="stats-grid">
          <div className="stat">
            <div className="stat-label">Signatures this period</div>
            <div className="stat-value">{formatCount(signaturesUsed)}</div>
            <ProgressBar
              used={signaturesUsed}
              cap={customer.signature_quota_monthly}
              label="quota"
            />
          </div>
          <div className="stat">
            <div className="stat-label">Receipt library</div>
            <div className="stat-value">{formatBytes(libraryBytesUsed)}</div>
            <ProgressBar
              used={libraryBytesUsed}
              cap={customer.receipt_library_quota_bytes}
              label="included"
              formatValue={formatBytes}
            />
          </div>
          <div className="stat">
            <div className="stat-label">Current tier</div>
            <div className="stat-value">{formatTier(customer.tier)}</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              {tierTagline(customer.tier)}
            </div>
          </div>
        </div>
      </div>

      <div className="panel">
        <h2 className="section-title">What's next</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Sequesign produces signed, verifiable receipts of agent work. The SDK drops into your
          existing agent code; receipts verify offline against published keys.
        </p>
        <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
          <a className="btn" href={DOCS_URL} target="_blank" rel="noreferrer">
            Read the docs
          </a>
          <a className="btn btn-secondary" href={SDK_URL} target="_blank" rel="noreferrer">
            Try the SDK
          </a>
          <button className="btn btn-secondary" onClick={() => props.onNavigate("/keys")}>
            Manage API keys
          </button>
        </div>
      </div>
    </>
  );
}
